/**
 * Phone number validation for React Hook Form
 * Validates 10-digit US phone numbers
 * Format: Only numerical digits (0-9)
 * Regex pattern: ^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$
 */
export const phoneNumberValidation = (errorMsg: string) => ({
  validate: {
    validPhoneNumber: (value: string) => {
      if (!value || value.length === 0) return true;

      const phonePattern = /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
      const digitsOnly = value.replace(/\D/g, "");

      return (phonePattern.test(value) && digitsOnly.length === 10) || errorMsg;
    },
  },
});

/**
 * Email validation for React Hook Form
 * Format: Must contain '@' symbol and period ('.')
 * Regex pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
 */
export const emailValidation = (errorMsg: string) => ({
  validate: {
    validEmail: (value: string) => {
      if (!value || value.length === 0) return true;

      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      return emailPattern.test(value) || errorMsg;
    },
  },
});

/**
 * Name validation for React Hook Form
 * Allows letters, hyphens, apostrophes
 * Rules:
 * - No consecutive identical special characters (e.g., --, '')
 * - Must contain at least one letter (not only special characters)
 * - Maximum length: configurable (default 50 characters)
 * @param genericErrorMsg - Generic error message for format issues (e.g., "Please enter a valid first name")
 * @param characterErrorMsg - Specific error message for invalid characters (e.g., "Name may only contain letters, hyphens or apostrophes.")
 * @param maxLength - Maximum character length (default: 50)
 */
export const nameValidation = (
  genericErrorMsg: string,
  characterErrorMsg?: string,
  maxLength: number = 50
) => ({
  maxLength: {
    value: maxLength,
    message: `Maximum ${maxLength} characters allowed`,
  },
  validate: {
    validName: (value: string) => {
      if (!value || value.length === 0) return true;

      // Check if name contains only valid characters (letters, hyphens, apostrophes, spaces)
      const namePattern = /^[a-zA-Z'\s-]+$/;
      if (!namePattern.test(value)) {
        return characterErrorMsg || genericErrorMsg;
      }

      // Check if name contains at least one letter
      if (!/[a-zA-Z]/.test(value)) {
        return genericErrorMsg;
      }

      // Check for consecutive identical special characters (e.g., --, '')
      if (/--/.test(value) || /''/.test(value)) {
        return genericErrorMsg;
      }

      return true;
    },
  },
});

/**
 * Date of birth validation for React Hook Form.
 * Accepts either the native `<input type="date">` value (`YYYY-MM-DD`) or the
 * legacy US display format (`MM/DD/YYYY`) so this validator keeps working if
 * a form still uses a plain text input. Sanity-checks the shape and the
 * month/day ranges, then enforces the 18+ age requirement. Not a full
 * calendar validator — e.g. `1990-02-31` still passes the range check
 * because `new Date` silently rolls it into March; the native picker won't
 * emit that shape, and the legacy branch inherited the same gap.
 * @param ageErrorMsg - Error message for age validation (e.g., "You must be 18 years or older")
 * @param formatErrorMsg - Error message for format validation
 */
export const dateOfBirthValidation = (
  ageErrorMsg: string,
  formatErrorMsg?: string
) => ({
  validate: {
    validDateFormat: (value: string) => {
      if (!value) return true;

      const formatError = formatErrorMsg || "Invalid date";

      // Native `<input type="date">` yields ISO `YYYY-MM-DD`.
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      // Legacy `<input type="text">` display format.
      const usMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

      let year: number;
      let month: number;
      let day: number;

      if (isoMatch) {
        year = parseInt(isoMatch[1], 10);
        month = parseInt(isoMatch[2], 10);
        day = parseInt(isoMatch[3], 10);
      } else if (usMatch) {
        month = parseInt(usMatch[1], 10);
        day = parseInt(usMatch[2], 10);
        year = parseInt(usMatch[3], 10);
      } else {
        return formatError;
      }

      if (month < 1 || month > 12) return formatError;
      if (day < 1 || day > 31) return formatError;

      // Check if person is 18 years or older
      const birthDate = new Date(year, month - 1, day);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();

      // Check if birthday has occurred this year
      const hasHadBirthdayThisYear =
        monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0);
      const actualAge = hasHadBirthdayThisYear ? age : age - 1;

      if (actualAge < 18) return ageErrorMsg;

      return true;
    },
  },
});
